import { KeyboardArrowDown } from '@mui/icons-material';
import {
  Box,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { ClearIcon } from '@mui/x-date-pickers';
import CustomButton from 'components/shared/CustomButton/CustomButton';
import CustomTextField from 'components/shared/CustomTextField/CustomTextField';
import SectionLoader from 'components/shared/SectionLoader';
import { FieldArray, useFormikContext } from 'formik';
import { useEffect, useState } from 'react';
import { DealsService } from 'service/deals.service';
import zeplinColor from 'theme/Color';
import { Utilities } from 'utilities/utilities';
import { Service } from './types';

const Services = () => {
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down('xl'));

  const { values, handleChange, handleBlur } = useFormikContext<any>();
  const { dealTypeId } = values;
  const [services, setServices] = useState<Service[]>();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!dealTypeId) return;

    const fetchServices = async () => {
      try {
        setLoading(true);
        const data = await DealsService.getServicesByDealType(dealTypeId);
        setServices(data.data);
      } catch (error) {
        console.error('No such services for the required deal type');
      } finally {
        setLoading(false);
      }
    };

    fetchServices();
  }, [dealTypeId]);

  return (
    <FieldArray name='services'>
      {({ push, remove }) => (
        <>
          {values.services.map((item: any, index: number) => (
            <Box
              key={`services.${index}.serviceName`}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                gap: '24px',
              }}
            >
              {index < values.services.length && index !== 0 && (
                <Divider sx={{ borderColor: zeplinColor.Neutral6 }} />
              )}

              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '24px',
                }}
              >
                <FormControl fullWidth sx={{ width: '100%', minWidth: 0 }}>
                  <InputLabel
                    size={isXs ? 'small' : 'medium'}
                    sx={{
                      fontSize: isXs ? 14 : 16,
                      '& .MuiFormLabel-asterisk': {
                        color: zeplinColor.Error,
                      },
                    }}
                  >
                    Select Service
                  </InputLabel>
                  <Select
                    disabled={!dealTypeId}
                    IconComponent={KeyboardArrowDown}
                    name={`services.${index}.serviceName`}
                    value={item.serviceName}
                    label={'Select Service'}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    size={isXs ? 'small' : 'medium'}
                    sx={{
                      width: '100%',
                      minWidth: 0,
                      '& .MuiSelect-select': {
                        fontSize: isXs ? 14 : 16,
                      },
                    }}
                    endAdornment={
                      item.serviceName && (
                        <IconButton
                          size='small'
                          sx={{
                            marginRight: 2,
                            visibility: item.serviceName ? 'visible' : 'hidden',
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleChange({
                              target: { name: `services.${index}.serviceName`, value: '' },
                            });
                          }}
                        >
                          <ClearIcon fontSize='small' />
                        </IconButton>
                      )
                    }
                  >
                    {loading ? (
                      <SectionLoader />
                    ) : (
                      services?.length &&
                      services.map((opt) => (
                        <MenuItem key={opt.serviceId} value={opt.serviceName}>
                          {opt.serviceName}
                        </MenuItem>
                      ))
                    )}
                  </Select>
                </FormControl>
                <Box
                  sx={{
                    display: 'inline-flex',
                    justifyContent: 'space-between',
                    gap: '24px',
                  }}
                >
                  {['floor', 'target', 'ceil'].map((field) => (
                    <CustomTextField
                      style={{ flex: 1 }}
                      text={item[field] > 0 ? item[field] : null}
                      type={'number'}
                      label={`${Utilities.capitalize(field)} Price`}
                      name={`services.${index}.${field}`}
                      onChange={handleChange}
                      form={true}
                    />
                  ))}
                </Box>
              </Box>

              {index !== 0 && (
                <CustomButton
                  disabled={false}
                  variant='text'
                  text={'Remove Service'}
                  style={{
                    alignSelf: 'flex-start',
                  }}
                  onClick={() => remove(index)}
                />
              )}
            </Box>
          ))}
          <CustomButton
            disabled={false}
            variant='text'
            text={'Add Service'}
            onClick={() => push({ serviceName: '', floor: 0, target: 0, ceil: 0 })}
          />
        </>
      )}
    </FieldArray>
  );
};

export default Services;
